/**
 * MetaTrader 5–style menu icons (visual only).
 * Charcoal outlines + colored accents where MT5 uses them (e.g. sky-blue moon).
 */
import React from "react";

const C = "currentColor";

function Svg({ children, className = "", viewBox = "0 0 24 24", size = 18 }) {
  return (
    <svg
      className={`mt5-menu-svg ${className}`.trim()}
      viewBox={viewBox}
      width={size}
      height={size}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** Trading accounts — head in circle */
export function IconAccounts() {
  return (
    <Svg>
      <circle cx="12" cy="12" r="9" stroke={C} strokeWidth="1.6" />
      <circle cx="12" cy="9.2" r="2.6" stroke={C} strokeWidth="1.5" />
      <path
        d="M6.8 17.6c1.2-2.2 2.9-3.3 5.2-3.3s4 1.1 5.2 3.3"
        stroke={C}
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </Svg>
  );
}

/** Chart settings — dual candlesticks */
export function IconChartSettings() {
  return (
    <Svg>
      <path d="M8 5v14" stroke={C} strokeWidth="1.5" strokeLinecap="round" />
      <rect x="5.5" y="8" width="5" height="8" rx="0.6" fill={C} />
      <path d="M16 4v16" stroke={C} strokeWidth="1.5" strokeLinecap="round" />
      <rect x="13.5" y="7" width="5" height="9" rx="0.6" stroke={C} strokeWidth="1.5" />
    </Svg>
  );
}

/** Wallet — MT5-like circular arrow + cursor (one-click style) */
export function IconWallet() {
  return (
    <Svg>
      <path
        d="M17.5 8.2A6.5 6.5 0 1 0 18 14.2"
        stroke={C}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path
        d="M17.2 5.2v3.6h-3.5"
        stroke={C}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.2 10.2l5.8 2.1-2.5 1.1-1.1 2.5-2.2-5.7z"
        fill={C}
      />
    </Svg>
  );
}

/** Wallet → Overview — purse / balance card */
export function IconWalletOverview() {
  return (
    <Svg>
      <rect x="3.5" y="7" width="17" height="11.5" rx="2" stroke={C} strokeWidth="1.55" />
      <path d="M3.5 10.2h17" stroke={C} strokeWidth="1.45" />
      <circle cx="15.8" cy="14.2" r="1.55" stroke={C} strokeWidth="1.4" />
      <path
        d="M7.2 5.8c0-1.2.9-2.2 2.1-2.2h5.4c1.2 0 2.1 1 2.1 2.2"
        stroke={C}
        strokeWidth="1.45"
        strokeLinecap="round"
      />
    </Svg>
  );
}

/** Wallet → Add funds — deposit / arrow into wallet */
export function IconWalletAddFunds() {
  return (
    <Svg>
      <rect x="3.5" y="8.5" width="17" height="10" rx="2" stroke={C} strokeWidth="1.55" />
      <path d="M15.2 13.5h3.6" stroke={C} strokeWidth="1.45" strokeLinecap="round" />
      <path
        d="M12 3.8v8.2M12 12l-2.4-2.4M12 12l2.4-2.4"
        stroke={C}
        strokeWidth="1.55"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

/** Wallet → Withdraw — cash out / arrow leaving wallet */
export function IconWalletWithdraw() {
  return (
    <Svg>
      <rect x="3.5" y="8.5" width="17" height="10" rx="2" stroke={C} strokeWidth="1.55" />
      <path d="M15.2 13.5h3.6" stroke={C} strokeWidth="1.45" strokeLinecap="round" />
      <path
        d="M12 15.2V7M12 7l-2.4 2.4M12 7l2.4 2.4"
        stroke={C}
        strokeWidth="1.55"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

/** Wallet → Statement — document / ledger */
export function IconWalletStatement() {
  return (
    <Svg>
      <path
        d="M7 3.5h7.2L18.5 8v12.5a1.5 1.5 0 0 1-1.5 1.5H7A1.5 1.5 0 0 1 5.5 20.5v-15A1.5 1.5 0 0 1 7 3.5z"
        stroke={C}
        strokeWidth="1.55"
        strokeLinejoin="round"
      />
      <path d="M14 3.6V8h4.3" stroke={C} strokeWidth="1.45" strokeLinejoin="round" />
      <path
        d="M8.5 12h7M8.5 15h7M8.5 18h4.5"
        stroke={C}
        strokeWidth="1.45"
        strokeLinecap="round"
      />
    </Svg>
  );
}

/**
 * MT5 Dark Theme icon (exact visual target):
 * - Solid sky-blue crescent with body on the RIGHT (opens LEFT)
 * - Four equal sky-blue square stars in a 2×2 grid in the left cradle
 */
export function IconDarkTheme() {
  const blue = "#6DB3FF";
  const maskId = `mt5-moon-${React.useId().replace(/:/g, "")}`;
  return (
    <Svg viewBox="0 0 20 20" className="mt5-menu-svg--moon">
      <defs>
        <mask id={maskId}>
          <rect width="20" height="20" fill="#ffffff" />
          {/* Cut from the LEFT → thick crescent body remains on the RIGHT */}
          <circle cx="7.2" cy="10" r="6.1" fill="#000000" />
        </mask>
      </defs>
      <circle cx="11.6" cy="10" r="7.2" fill={blue} mask={`url(#${maskId})`} />
      {/* 2×2 stars in the left cradle (same blue as moon) */}
      <rect x="2.4" y="6.6" width="1.55" height="1.55" fill={blue} />
      <rect x="4.6" y="6.6" width="1.55" height="1.55" fill={blue} />
      <rect x="2.4" y="8.8" width="1.55" height="1.55" fill={blue} />
      <rect x="4.6" y="8.8" width="1.55" height="1.55" fill={blue} />
    </Svg>
  );
}

/** Light theme — sky-blue sun (when already in dark mode) */
export function IconLightTheme() {
  const blue = "#6DB3FF";
  return (
    <Svg viewBox="0 0 20 20">
      <circle cx="10" cy="10" r="3.2" fill={blue} />
      <path
        d="M10 2.6v1.9M10 15.5v1.9M2.6 10h1.9M15.5 10h1.9M4.8 4.8l1.35 1.35M13.85 13.85l1.35 1.35M15.2 4.8l-1.35 1.35M6.15 13.85l-1.35 1.35"
        stroke={blue}
        strokeWidth="1.45"
        strokeLinecap="round"
      />
    </Svg>
  );
}

/**
 * MT5 Color Templates icon:
 * - Charcoal circular palette + thumb hole bottom-right
 * - Three paint wells (top-left cluster)
 * - Brush from bottom-right pointing into center
 */
export function IconColorTemplates() {
  const ink = "currentColor";
  return (
    <Svg viewBox="0 0 20 20" className="mt5-menu-svg--palette">
      {/* Kidney palette with built-in thumb notch (no white fill) */}
      <path
        d="M9.6 2.9c-3.9 0-7 2.85-7 6.45 0 2.35 1.35 4.35 3.4 5.45.35-.75.95-1.25 1.75-1.25.55 0 1.05.25 1.4.65.4.45.95.7 1.6.7 3 0 5.35-2.45 5.35-5.55 0-3.15-2.45-5.45-5.5-5.45z"
        stroke={ink}
        strokeWidth="1.45"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Three paint wells */}
      <circle cx="6.5" cy="7.1" r="1" fill={ink} />
      <circle cx="9.4" cy="5.95" r="1" fill={ink} />
      <circle cx="7.35" cy="9.55" r="1" fill={ink} />
      {/* Brush from bottom-right into palette (MT5) */}
      <path
        d="M12.4 11.5l5.1 5.35"
        stroke={ink}
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path d="M11.35 10.35l2.55 2.45-1.1 1.15-2.5-2.4z" fill={ink} />
    </Svg>
  );
}

/** History — scroll */
export function IconHistory() {
  return (
    <Svg>
      <path
        d="M8 4.5h8.5c1.2 0 2 .9 2 2v11c0 1.1-.8 2-2 2H8"
        stroke={C}
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M8 4.5c-1.5 0-2.5.9-2.5 2v1.2c0 .6.4 1 1 1h1.5M8 19.5c-1.5 0-2.5-.9-2.5-2v-1.2c0-.6.4-1 1-1H8"
        stroke={C}
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path d="M10.5 9h5M10.5 12h5M10.5 15h3.5" stroke={C} strokeWidth="1.4" strokeLinecap="round" />
    </Svg>
  );
}

/** Position report — document */
export function IconPosition() {
  return (
    <Svg>
      <path
        d="M7.5 4.5h7l3 3v12c0 .8-.7 1.5-1.5 1.5h-8.5c-.8 0-1.5-.7-1.5-1.5v-13c0-.8.7-1.5 1.5-1.5z"
        stroke={C}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path d="M14.5 4.5v3.2h3" stroke={C} strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M9 12h6.5M9 15h6.5M9 18h4" stroke={C} strokeWidth="1.4" strokeLinecap="round" />
    </Svg>
  );
}

/** Banned — no-entry */
export function IconBanned() {
  return (
    <Svg>
      <circle cx="12" cy="12" r="8.2" stroke={C} strokeWidth="1.6" />
      <path d="M6.8 6.8l10.4 10.4" stroke={C} strokeWidth="1.7" strokeLinecap="round" />
    </Svg>
  );
}

/** Max quantity — balance scales */
export function IconMaxQty() {
  return (
    <Svg>
      <path d="M12 4.5v13.5M8 18.5h8" stroke={C} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M5.5 9h13" stroke={C} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M5.5 9l-2.2 5.2c.8 1.4 2.1 2 3.5 2s2.7-.6 3.5-2L8 9" stroke={C} strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M16 9l-2.2 5.2c.8 1.4 2.1 2 3.5 2s2.7-.6 3.5-2L18.5 9" stroke={C} strokeWidth="1.4" strokeLinejoin="round" />
      <circle cx="12" cy="4.8" r="1.2" fill={C} />
    </Svg>
  );
}

/** Reports — bars */
export function IconReports() {
  return (
    <Svg>
      <path d="M5 19.5h14" stroke={C} strokeWidth="1.5" strokeLinecap="round" />
      <rect x="6" y="11" width="3.2" height="6.5" rx="0.4" fill={C} />
      <rect x="10.4" y="7.5" width="3.2" height="10" rx="0.4" fill={C} />
      <rect x="14.8" y="9.5" width="3.2" height="8" rx="0.4" stroke={C} strokeWidth="1.4" />
    </Svg>
  );
}

/** Reports → Trade Report — candlestick trade */
export function IconReportTrade() {
  return (
    <Svg>
      <path d="M8 4.5v15" stroke={C} strokeWidth="1.5" strokeLinecap="round" />
      <rect x="5.6" y="8" width="4.8" height="7" rx="0.5" fill={C} />
      <path d="M16.5 5.5v13" stroke={C} strokeWidth="1.5" strokeLinecap="round" />
      <rect x="14.1" y="9.5" width="4.8" height="6" rx="0.5" stroke={C} strokeWidth="1.45" />
    </Svg>
  );
}

/** Reports → Ledger Report — open ledger book */
export function IconReportLedger() {
  return (
    <Svg>
      <path
        d="M12 5.2c-1.6-1-3.4-1.4-5.2-1.4H4.5v14.4H6.8c1.8 0 3.6.4 5.2 1.4 1.6-1 3.4-1.4 5.2-1.4h2.3V3.8H17.2c-1.8 0-3.6.4-5.2 1.4z"
        stroke={C}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path d="M12 5.2v14.4" stroke={C} strokeWidth="1.45" strokeLinecap="round" />
      <path
        d="M6.2 8.2h3.2M6.2 11h3.2M6.2 13.8h2.4M14.6 8.2h3.2M14.6 11h3.2M14.6 13.8h2.4"
        stroke={C}
        strokeWidth="1.3"
        strokeLinecap="round"
      />
    </Svg>
  );
}

/** Reports → Rejection Log — document with X */
export function IconReportRejection() {
  return (
    <Svg>
      <path
        d="M7.2 3.8h7.1L18.2 7.8v12.4a1.4 1.4 0 0 1-1.4 1.4H7.2A1.4 1.4 0 0 1 5.8 20.2V5.2A1.4 1.4 0 0 1 7.2 3.8z"
        stroke={C}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path d="M14.2 3.9V7.7h3.8" stroke={C} strokeWidth="1.4" strokeLinejoin="round" />
      <path
        d="M9.2 12.2l5.2 5.2M14.4 12.2l-5.2 5.2"
        stroke={C}
        strokeWidth="1.55"
        strokeLinecap="round"
      />
    </Svg>
  );
}

/** Reports → Edit/Delete Log — document with pencil */
export function IconReportEditDelete() {
  return (
    <Svg>
      <path
        d="M6.8 3.8h6.8L17.5 7.6v12.6a1.4 1.4 0 0 1-1.4 1.4H6.8A1.4 1.4 0 0 1 5.4 20.2V5.2A1.4 1.4 0 0 1 6.8 3.8z"
        stroke={C}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path d="M13.5 3.9V7.5h3.7" stroke={C} strokeWidth="1.4" strokeLinejoin="round" />
      <path
        d="M8.2 16.8l6.6-6.6 1.7 1.7-6.6 6.6H8.2v-1.7z"
        stroke={C}
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

/** Reports → Trial Balance — T-account / equal scales */
export function IconReportTrialBalance() {
  return (
    <Svg>
      <path d="M12 4.2v14.2M7.5 18.8h9" stroke={C} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M5 8.2h14" stroke={C} strokeWidth="1.5" strokeLinecap="round" />
      <path
        d="M5 8.2l-1.8 4.6c.7 1.2 1.8 1.8 3.1 1.8s2.4-.6 3.1-1.8L7.6 8.2"
        stroke={C}
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path
        d="M16.4 8.2l-1.8 4.6c.7 1.2 1.8 1.8 3.1 1.8s2.4-.6 3.1-1.8L19.2 8.2"
        stroke={C}
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path d="M9.8 12.8h4.4" stroke={C} strokeWidth="1.35" strokeLinecap="round" />
    </Svg>
  );
}

/** Shortcuts — keyboard / help circle with ? (MT5 help-like) */
export function IconShortcuts() {
  return (
    <Svg>
      <rect x="3.5" y="7" width="17" height="11" rx="2" stroke={C} strokeWidth="1.5" />
      <path
        d="M6.5 10h1.2M9.2 10h1.2M11.9 10h1.2M14.6 10h1.2M17.3 10H18.5M6.5 13h1.2M9.2 13h5.6M16.3 13h2.2M8 16h8"
        stroke={C}
        strokeWidth="1.35"
        strokeLinecap="round"
      />
    </Svg>
  );
}

/** Language — globe */
export function IconLanguage() {
  return (
    <Svg>
      <circle cx="12" cy="12" r="8.2" stroke={C} strokeWidth="1.6" />
      <path d="M4.2 12h15.6" stroke={C} strokeWidth="1.35" strokeLinecap="round" />
      <path
        d="M12 3.8c2.2 2.2 3.4 5.1 3.4 8.2S14.2 18 12 20.2C9.8 18 8.6 15.1 8.6 12S9.8 6 12 3.8z"
        stroke={C}
        strokeWidth="1.35"
      />
    </Svg>
  );
}

/** About — i in circle */
export function IconAbout() {
  return (
    <Svg>
      <circle cx="12" cy="12" r="8.2" stroke={C} strokeWidth="1.6" />
      <path d="M12 10.4v5.2" stroke={C} strokeWidth="1.7" strokeLinecap="round" />
      <circle cx="12" cy="7.8" r="1.05" fill={C} />
    </Svg>
  );
}

/** Logout — door with arrow */
export function IconLogout() {
  return (
    <Svg>
      <path
        d="M10 5.5H7.5A2 2 0 0 0 5.5 7.5v9A2 2 0 0 0 7.5 18.5H10"
        stroke={C}
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M12 12h7M16.2 8.8L19.5 12l-3.3 3.2"
        stroke={C}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

const BY_ID = {
  accounts: IconAccounts,
  chartSettings: IconChartSettings,
  wallet: IconWallet,
  walletOverview: IconWalletOverview,
  walletAddFunds: IconWalletAddFunds,
  walletWithdraw: IconWalletWithdraw,
  walletStatement: IconWalletStatement,
  darkTheme: IconDarkTheme,
  lightTheme: IconLightTheme,
  colorTemplates: IconColorTemplates,
  history: IconHistory,
  position: IconPosition,
  banned: IconBanned,
  maxqty: IconMaxQty,
  reports: IconReports,
  reportTrade: IconReportTrade,
  reportLedger: IconReportLedger,
  reportRejection: IconReportRejection,
  reportEditDelete: IconReportEditDelete,
  reportTrialBalance: IconReportTrialBalance,
  shortcuts: IconShortcuts,
  language: IconLanguage,
  about: IconAbout,
  logout: IconLogout,
};

/** Resolve menu icon by id (keeps labels/actions elsewhere unchanged). */
export function Mt5MenuIcon({ id, className = "" }) {
  const Comp = BY_ID[id] || IconAbout;
  return (
    <span className={`menu-icon menu-icon--svg ${className}`.trim()} aria-hidden>
      <Comp />
    </span>
  );
}

export default Mt5MenuIcon;
